import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthCheck } from '../utils/useAuthCheck';
import { usePermissions } from '../hooks/usePermissions';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useRouterProps } from '../hooks/useRouterProps';
import { ROUTES } from '@lsgo/lsgo-common/constants/routes';

/**
 * Base route guard component that combines authentication, permissions, roles, and feature flags
 * All other guards extend or use this component
 */
const RouteGuard = ({
    requiresAuth = false,
    permissions = [],
    requireAllPermissions = false,
    roles = [],
    featureFlags = [],
    requireAllFlags = false,
    redirectTo,
    fallback,
    children,
    render,
    embed = false,
    allowExpired = false,
    ...props
}) => {
    const permissionsHook = usePermissions();
    const featureFlagsHook = useFeatureFlags();
    const routerProps = useRouterProps();
    const authRedirect = useAuthCheck({ requiresAuth, allowExpired, embed, redirectTo });

    // Check authentication (handles embed mode internally)
    if (authRedirect) {
        return authRedirect;
    }

    // Check permissions
    if (permissions && permissions.length > 0) {
        const hasAccess = requireAllPermissions
            ? permissionsHook.hasAllPermissions(permissions)
            : permissionsHook.hasAnyPermission(permissions);

        if (!hasAccess) {
            return fallback || <Navigate to={redirectTo || ROUTES.AUTH.NO_PERMISSION.path} replace />;
        }
    }

    // Check roles
    if (roles && roles.length > 0) {
        if (!permissionsHook.hasAnyRole(roles)) {
            return fallback || <Navigate to={redirectTo || ROUTES.AUTH.NO_PERMISSION.path} replace />;
        }
    }

    // Check feature flags
    if (featureFlags && featureFlags.length > 0) {
        const hasFlags = requireAllFlags
            ? featureFlagsHook.hasAllFeatureFlags(featureFlags)
            : featureFlagsHook.hasAnyFeatureFlag(featureFlags);

        if (!hasFlags) {
            return fallback || <Navigate to={redirectTo || ROUTES.AUTH.FEATURE_NOT_AVAILABLE.path} replace />;
        }
    }

    // All checks passed, render children
    return render ? render({ ...props, ...routerProps }) : children;
};

export default RouteGuard;
